/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {EventEmitter, forwardRef, Inject, Injectable} from '@angular/core';
import {AsyncValidatorFn, UntypedFormControl, ValidationErrors} from '@angular/forms';

import {BehaviorSubject, forkJoin, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {catchError, map, mergeMap, switchMap, tap} from 'rxjs/operators';

import * as moment from 'moment/moment';

import {ProfileType} from './contract/contract.interfaces';
import {
  Contract,
  EmpFile,
  EmpFilesFolder,
  Employee,
  EmployeeDetail,
  PayPeriodContract,
  SearchOptions
} from './employees.model';

import {ExtendDTO} from '../../+employees/extend/extend-contract-interfaces';
import {EmpZoneGroupEmpContractData} from '../../+mass-data-update/mass-data-update.interfaces';
import {Profile} from '../../+settings/user-rights/user-rights.interfaces';
import {
  DATE_FORMAT,
  DATE_MAX,
  DATE_MIN,
  ObserveType,
  ProfileMode,
  REQUEST_OPTIONS,
  ResponseType
} from '../../app.constants';
import {CliEmpAttributes, EmpContractAttributes} from '../../models/models.interfaces';
import {mapToFileModel} from '../../shared/utils/files';
import {ProcessInstance} from '../../shared/workflow/workflow-v2.interface';
import {ApiListData, ApiService, Data, DataListAndCount} from '../api/';
import {ContractFlattenDTO, EmpContractWrapper} from '../api/GaApi';
import {RolesService} from '../authentication/roles/roles.service';
import {Image} from '../files/';
import {DEFAULT_DELETE_FILE_URL} from '../files/file-changes.service';
import {FormValues, SimulationContractData, ZoneValues} from '../form-generator/form-generator.interfaces';
import {ListValue} from '../hire/hire.interface';
import {Headers} from '../utils/headers';
import {isPresent} from '../utils/lang';
import {QueryParamBuilder} from '../utils/query-param-builder';
import {getWorkflowKey, ONBOARDING_WORKFLOW, WorkflowV2Service} from '../workflow/workflow-v2.service';

export const getContractWithLatestEndDate = (employee: Data<CliEmpAttributes>, mainContract) => {
  return employee.empContracts.reduce((contract1, contract2) => {
    if (moment(contract1.enddate).isAfter(moment(contract2.enddate))) {
      return contract1;
    } else if (moment(contract2.enddate).isAfter(moment(contract1.enddate))) {
      return contract2;
    }
    return mainContract;
  });
};

const PER_PAGE = 100;

/**
 * Employees service that expose access to employee information.
 */
@Injectable()
export class EmployeesService {

  public employeeChanged$: Subject<Employee> = new Subject<Employee>();
  public selectedEmployeeContracts = new ReplaySubject<Array<Data<EmpContractAttributes>>>(1);
  public editListEmployee: EventEmitter<any> = new EventEmitter<any>();
  public empFiles$: BehaviorSubject<Array<EmpFile>> = new BehaviorSubject([]);
  public removeFile$: EventEmitter<EmpFile> = new EventEmitter<EmpFile>();

  public contractChanged$: Subject<number> = new Subject();

  private readonly recalculationRequested: Subject<void> = new Subject<void>();

  private readonly employeeFileActionButtonsDisabled: Subject<{ action: string; value: boolean }> = new Subject<{
    action: string;
    value: boolean
  }>();

  constructor(@Inject(forwardRef(() => RolesService)) private rolesService: RolesService,
              private api: ApiService,
              private workflowV2Service: WorkflowV2Service) {
  }

  public getOnboardingValues(contractId: number): Observable<ZoneValues[]> {
    return this.api.get(`/ga/api/v1/empData/getTempEmpDataByContractId?contractId=${contractId}`);
  }

  public getNewOnboardingValues(contractId: number): Observable<ZoneValues[]> {
    return this.api.get(`/ga/api/v2/onboarding/empContract/${contractId}`).pipe(
      map((response) => response?.data?.attributes?.empDatas)
    );
  }

  /**
   * Returns employee details for an employee id
   * @param id the id of the employee to get details for
   * @param short is flag for getting only brief info about employee
   * @returns {Observable<Employee>} an observables that emits the employee details when available
   */
  public getEmployee(id: number, short = false): Observable<Employee> {
    let uri = `/ga/api/v1/cliEmp/${id}`;
    if (short) {
      uri = uri + '?view=short';
    }
    return this.api.get(uri)
      .pipe(
        map(result => {
          const detail = <EmployeeDetail>result;
          return new Employee(detail.data, detail.included);
        })
      );
  }

  public getEmployeeById(employeeId: number): Observable<Employee & { links: any }> {
    return this.api.get(`/ga/api/v1/cliEmp?seeInactiveEmployees=true&search=id==${employeeId}`)
      .pipe(map(result => result.data[0]));
  }

  public getPartialEmployee(id: number): Observable<Employee> {
    return this.api.get(`/ga/api/v1/cliEmp/getPartialInfo/${id}`);
  }

  public getOptionsForCopy(cliRegId: number): Observable<ListValue[]> {
    return this.api.get(`/repository/api/v1/cliReg/getOptionsForCopyInfo`, {
      requestOptions: {params: {cliRegId}}
    });
  }

  /**
   * List or search all employees.
   *
   * @param options {SearchOptions} the options to list or search employees with
   */
  public getAllEmployees(options: SearchOptions = {},
                         path = '/ga/api/v1/cliEmp'): Observable<DataListAndCount<CliEmpAttributes>> {
    const queryString = Object.keys(options)
      .filter(key => options[key] !== undefined)
      .map(option => `${option}=${options[option]}`)
      .join('&');

    return this.api.get(`${path}?${queryString}`, REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map(result => {
          return <DataListAndCount<CliEmpAttributes>>{
            items: result.status === 204 ? {data: []} : <ApiListData<CliEmpAttributes>>result.body,
            total: parseInt(Headers.get(result.headers, 'X-Total-Count'), 10)
          };
        })
      );
  }

  public getDuplicates(params: string, profileMode = ProfileMode.CLASSIC): Observable<Array<Data<CliEmpAttributes>>> {
    return this.api.get(`/ga/api/v1/cliEmp/check-duplicates?profile-mode=${profileMode}&${params}`).pipe(
      switchMap((duplicates: Array<Data<CliEmpAttributes>>) => forkJoin(duplicates.map(employee => {
          employee.hasOngoingContract = !!employee.empContracts.find((contract: Contract) =>
            moment().isBetween(moment(contract.begindate), moment(contract.enddate)));
          return of(employee);
        })
      )));
  }

  public getPaySerieByContract(contract: Contract) {
    return this.api.get(`/ga/api/v1/empContract/${contract.id}/paySerie`)
      .pipe(
        mergeMap(entityResult => {
          if (entityResult && entityResult.paySerieId) {
            return this.api.get(`/payroll/api/v1/paySerie/${entityResult.paySerieId}`);
          }
          return of(null);
        })
      );
  }

  public deletePicture(employee: Employee): Observable<Response> {
    const photoLink = employee.photoLink;

    if (!isPresent(photoLink)) {
      return Observable.throw(new Error('Cannot delete photo : no URI provided by the server'));
    }

    return this.api.deleteFile(photoLink.href);
  }

  /**
   * Change the pictures of an employee : upload the file and replaces the photo link in the employee data.
   *
   * @param employee {Employee} the employee to change the picture for
   * @param image {Image} the image to upload
   */
  public changePicture(employee: Employee, image: Image): Observable<any> {
    const uploadLink = employee.updatePhotoLink;

    if (!isPresent(uploadLink)) {
      return Observable.throw(new Error('Cannot change photo : no URI provided by the server'));
    }

    return this.api.uploadFile(uploadLink.href, image.file, {
      requestOptions: REQUEST_OPTIONS.OBSERVE.RESPONSE.requestOptions,
      additionalData: {
        width: image.dimensions.width,
        height: image.dimensions.height
      },
      fileName: image.fileName
    }).pipe(
      tap(response => {
        if (response.status === 201) {
          const location = Headers.get(response.headers, 'Location');
          const newLocation = location.split('/').slice(3).join('/');
          employee.setPhotoUrl('/' + newLocation);
        }
      })
    );
  }

  public getContract(id: number, isEss = false): Observable<Data<EmpContractWrapper>> {
    const params: string = isEss ? '?isEss=true' : '';
    return this.api.get(`/ga/api/v1/empContract/${id}${params}`).pipe(map(result => result.data));
  }

  public getContractData(id: number): Observable<EmpContractWrapper> {
    return this.api.get(`/ga/api/v1/empContract/${id}/data`)
      .pipe(
        map((result: any) => {
          if (result) {
            return result;
          } else {
            return of({empty: true});
          }
        })
      );
  }

  public getDataForTerminate(id: number): Observable<EmpContractWrapper> {
    return this.api.get(`/ga/api/v1/empContract/${id}/data/end-of-contract`)
      .pipe(
        map((result: any) => {
          if (result) {
            return result;
          } else {
            return of({empty: true});
          }
        })
      );
  }

  public getContractDataForTasks(contractId: number, profileType?: ProfileType): Observable<Data<EmpContractAttributes>> {
    return this.api.silent.get(`/ga/api/v1/empContract/${contractId}?` +
      `isEss=${profileType === ProfileType.ESS}&isMss=${profileType === ProfileType.MSS}`)
      .pipe(map(result => result.data));
  }

  public updateContract(id: number, contractData: any) {
    return this.api.post(`/ga/api/v1/empContract/${id}`, contractData);
  }

  public updateContractData(id: number | string, data: { [group: string]: any }) {
    return this.api.post(`/ga/api/v1/empData/update/${id}/data`, data, {
      requestOptions: {
        observe: ObserveType.RESPONSE
      }
    })
      .pipe(
        map(response => ({isInPopulation: response.headers.get('x-is-in-population') === 'true'}))
      );
  }

  public simulateCalculation(id: number | string, data: SimulationContractData) {
    return this.api.post(`/ga/api/v1/empContract/${id}/preview?mode=calculation`, data);
  }

  public addContract(id: number | string, data: { [group: string]: any }, queryParams = ''): Observable<{
    data,
    isInPopulation: boolean
  }> {
    return this.api.post(`/ga/api/v1/empData/addContract/${id}/data${queryParams}`, data, {
      requestOptions: {
        observe: ObserveType.RESPONSE
      }
    })
      .pipe(
        map(response => ({data: response.body, isInPopulation: response.headers.get('x-is-in-population') === 'true'}))
      );
  }

  public terminate(id: number | string, data: { [group: string]: any }) {
    return this.api.post(`/ga/api/v1/empContract/${id}/terminate`, data);
  }

  public revertTermination(id: number | string) {
    return this.api.post(`/ga/api/v1/empContract/${id}/revert-termination`);
  }

  public startTerminateWorkflow(requestVariables: any, workflowName: string, cliRegId: number, contractNumber: string,
                                profileMode: ProfileMode, userProfile: Profile): Observable<ProcessInstance> {
    const workflowKey = getWorkflowKey(workflowName, cliRegId);
    return this.workflowV2Service.startProcessInstance(workflowKey, {
      request: requestVariables,
      groupName: requestVariables['groupName'],
      EndOFContractReason: requestVariables.values['EndOFContractReason'].value,
      profile: userProfile.name,
      contractNumber: contractNumber,
      contractEndDate: requestVariables.values['empContract.enddate'].value,
      groupZoneTransId: requestVariables.groupZoneTransId,
      cliRegId
    }, false, profileMode);

  }

  public extend(id: number | string, data: ExtendDTO): Observable<{
    data,
    isInPopulation: boolean
  }> {
    return this.api.post(`/ga/api/v1/empContract/${id}/extend`, data, {
      requestOptions: {
        observe: ObserveType.RESPONSE
      }
    })
      .pipe(
        map(response => ({data: response.body, isInPopulation: response.headers.get('x-is-in-population') === 'true'}))
      );
  }

  public updateEmployee(id: number, employee: CliEmpAttributes) {
    return this.api.post(`/ga/api/v1/cliEmp/${id}`, employee);
  }

  public createEmployee(data: any) {
    return this.api.post(`/ga/api/v1/empData/create`, data, {
      requestOptions: {
        observe: ObserveType.RESPONSE
      }
    })
      .pipe(
        map(response => {
          const isInPopulation: boolean = response.headers.get('x-is-in-population') === 'true';
          return {data: response.body, isInPopulation};
        })
      );
  }


  public getContractOngoings(contractId: number): Observable<PayPeriodContract[]> {
    return this.api.get(`/payroll/api/v1/ongoing/byContract/${contractId}`)
      .pipe(
        catchError(() => of(null))
      );
  }

  public getOngoingPayPeriodContracts(cliRegId: number, contractId: number) {
    let payPeriodContracts: Observable<PayPeriodContract[]> = of([]);

    const hasRole: boolean = this.rolesService.hasRole('PAYROLL_READ', 'PAYROLL_WRITE');
    if (hasRole) {
      payPeriodContracts = this.getContractOngoings(contractId);
    }
    return payPeriodContracts;
  }

  public getCliEmp(contractId: number): Observable<any> {
    return this.api.get(`/ga/api/v1/cliEmp/byContract/${contractId}`);
  }

  public uploadFile(file: File, contractId: number, groupZone: string,
                    zone: string, groupZoneId: string, url?: string, authenticated = true,
                    profileMode: ProfileMode = ProfileMode.CLASSIC) {
    const requestOptions = Object.assign({}, REQUEST_OPTIONS.OBSERVE.RESPONSE.requestOptions);
    requestOptions.withCredentials = authenticated;
    requestOptions.params = {
      size: file.size,
      groupZone,
      zone,
      fileName: file.name,
      groupZoneId
    };
    return this.api.uploadFile(`${url || '/ga/api/v1/empFile/upload'}/${contractId}?profile-mode=${profileMode}`, file, {
      requestOptions,
      fileName: file.name
    });
  }

  public getEmpFiles(contractId: number, url?: string, authenticated = true, profileMode: ProfileMode = ProfileMode.CLASSIC): Observable<Array<EmpFile>> {
    return this.api.get(`${url || '/ga/api/v1/empFile/getEmpBinaryFiles'}/${contractId}?profile-mode=${profileMode}`,
      {
        requestOptions: {
          withCredentials: authenticated
        }
      })
      .pipe(
        map((files: EmpFile[]) => {
          this.empFiles$.next(files);

          return files;
        })
      );
  }

  public fetchAttachments(contractId: number,
                          formValues: FormValues = new Map(),
                          url?: string,
                          profileMode = ProfileMode.CLASSIC
  ): Observable<Array<EmpFilesFolder>> {

    return this.getEmpFiles(contractId, url, true, profileMode)
      .pipe(
        map((files: EmpFile[]) => {
          const values: any[] = [];
          formValues.forEach((groupZone: any) => {
            groupZone.forEach(value => {
              values.push(
                ...Object.keys(value.values)
                  .filter(key => value.values[key].type === 'FILE')
                  .map(key => value.values[key].value.toString())
              );
            });
          });

          const data: Map<string, EmpFilesFolder> = new Map();

          files.filter((f: EmpFile) => values.includes(f.id))
            .forEach((f: EmpFile) => {
              f.groupZone = f.groupZone || f['empZoneGroup'] || f['repZoneGroup'];
              f.zone = f.zone || f['empZone'] || f['repZone'];
              if (!data.has(f.groupZone)) {
                data.set(f.groupZone, {
                  groupZone: f.groupZone,
                  files: []
                });
              }

              data.get(f.groupZone).files.push(f);
            });

          data.forEach((groupZone: EmpFilesFolder) => {
            groupZone.files.forEach((f: EmpFile) => {
              if (!groupZone.updateDate || moment(f.updateDate).isAfter(groupZone.updateDate)) {
                groupZone.updateDate = f.updateDate;
              }
            });
          });

          return Array.from(data.values());
        })
      );
  }

  public removeFile(fileId: string, url?: string, authenticated = true, profileMode: ProfileMode = ProfileMode.CLASSIC): Observable<any> {
    return this.api.post(`${url || DEFAULT_DELETE_FILE_URL}/${fileId}?profile-mode=${profileMode}`, null,
      {
        requestOptions: {
          withCredentials: authenticated
        }
      });
  }

  public renameFile(fileId: string, name: string, url?: string, profileMode: ProfileMode = ProfileMode.CLASSIC): Observable<EmpFile> {
    return this.api.post(`${url || '/ga/api/v1/empFile/renameEmpBinaryFile'}/${fileId}?newName=${name}&profile-mode=${profileMode}`);
  }

  public getFile(file: EmpFile, url?: string, authenticated = true, profileMode: ProfileMode = ProfileMode.CLASSIC): Observable<any> {
    return this.api.get(
      `${url || '/ga/api/v1/empFile/downloadEmpBinaryFile'}/${file.id}?profile-mode=${profileMode}`,
      {
        requestOptions: {
          observe: ObserveType.RESPONSE,
          responseType: ResponseType.BLOB
        }
      }).pipe(
      map(mapToFileModel())
    );
  }

  public downloadFile(file: EmpFile, url?: string, authenticated = true, profileMode: ProfileMode = ProfileMode.CLASSIC): Observable<any> {
    return this.api.get(
      `${url || '/ga/api/v1/empFile/downloadEmpBinaryFile'}/${file.id}?profile-mode=${profileMode}`,
      {
        requestOptions: {
          responseType: ResponseType.BLOB,
          withCredentials: authenticated
        }
      }
    )
      .pipe(
        map((result: any) => {
          return new Blob([result]);
        }),
        map((blob: Blob) => {
          if ((navigator as any).msSaveOrOpenBlob) {
            (navigator as any).msSaveOrOpenBlob(blob, file.filename);
          } else {
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.setAttribute('href', blobUrl);
            a.setAttribute('download', file.filename);
            document.body.appendChild(a);
            a.dispatchEvent(new MouseEvent('click'));
            document.body.removeChild(a);
          }
        })
      );
  }

  public getPayPeriods(id: number): Observable<any> {
    return id === -1
      ? this.api.get(`/payroll/api/v1/payPeriod/listPayPeriodsForInternational`)
        .pipe(map((result) => result.data))
      : this.api.get(`/payroll/api/v1/payPeriod/actualPayPeriods?paySerieId=${id}&for-reporting=true`)
        .pipe(map((result) => result.data));
  }

  public getViewManagers(cliEmpId: number): Observable<any> {
    return this.api
      .get(`/ga/api/v1/peopleTree/viewManagers?cliEmpId=${cliEmpId}&effectiveDate=${moment().format(DATE_FORMAT)}`);
  }

  public checkEmployeeId(cliEmpOtherId: string): Observable<boolean> {
    return this.api.post(`/ga/api/v1/cliEmp/checkCliEmp?cliEmpOtherId=${encodeURIComponent(cliEmpOtherId)}`);
  }

  public checkContractId(empContractOtherId: string): Observable<boolean> {
    return this.api
      .post(`/ga/api/v1/empContract/checkEmpContract?empContractOtherId=${encodeURIComponent(empContractOtherId)}`);
  }

  public previewEmpContract(contractId: number, data: any): Observable<any> {
    return this.api.post(`/ga/api/v1/empContract/${contractId}/preview`, data);
  }


  public sendOnboardingV2(contractId: number, cliEmpId: number, formData: any, cliRegId: number,
                          profileMode: ProfileMode): Observable<any> {
    const workflowKey = getWorkflowKey(ONBOARDING_WORKFLOW, cliRegId);
    return this.workflowV2Service.startProcessInstance(workflowKey, {
        request: {
          formData,
          contractId,
          cliEmpId
        },
        cliRegId
      },
      false,
      profileMode);
  }

  public sendAskZone(contractId: number, zoneName: string, data): Observable<any> {
    return this.api.post(`/ga/api/v1/workflow/askZone/${contractId}/${zoneName}`, data);
  }

  public checkEmailAvailable(email: string, employeeId?: number): Observable<boolean> {
    return this.api.get(
      new QueryParamBuilder(`/authent/api/v1/cliUser/uniqueCheck/${email}`)
        .addIfNotNull('employeeId', employeeId).build());
  }

  public getPayPeriodContractsByContractId(contractIds: Array<number>): Observable<Array<PayPeriodContract>> {
    const requestParams = contractIds.map(id => `contract-id=${id}`).join('&');
    return this.api.get(`/payroll/api/v1/pay-period-contract?${requestParams}`);
  }

  public deleteContractsById(contractIds: Array<number>): Observable<Response> {
    const requestParams = contractIds.map(id => `contract-id=${id}`).join('&');
    return this.api.silent.delete(`/ga/api/v2/contract?${requestParams}`);
  }

  public getMainContractByCliEmpId(cliEmpId: number): Observable<Contract> {
    return this.api.get(`/ga/api/v2/contract?search=cliEmp.id==${cliEmpId};mainContract==true`)
      .pipe(map((contracts: Contract[]) => contracts.shift() || null));
  }

  public getOfferingDefaultProfile(): Observable<string> {
    return this.api.get('/repository/api/v1/global/offering/defaultProfile', REQUEST_OPTIONS.RESPONSE_TYPE.TEXT);
  }

  public fetchContractsWithDataByGroupName(
    empZoneGroupName: string,
    contractIds: number[],
    includeSimpleContracts: boolean,
    cliRegId: number,
    page = 0,
    perPage = 100
  ): Observable<{
    data: EmpZoneGroupEmpContractData[],
    total: number,
    currentPage: number,
    totalPages: number
  }> {
    const search = `id=in=(${(Array.from(contractIds).join(','))})`;
    const queryParams = `order-by=id&page=${page}&per-page=${perPage}&cliRegId=${cliRegId}&groupName=${empZoneGroupName}&includeSimpleContracts=${includeSimpleContracts}`;

    return this.api.ga20
      .post(`/contract/list-with-data-by-group-name?${queryParams}`,
        {search},
        REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map(response => {
          const total = parseInt(response.headers.get('x-total-count'), 10) || 0;
          const totalPages = Math.ceil(total / perPage);

          return {
            data: response.status === 204 ? [] : response.body,
            total: total,
            currentPage: page,
            totalPages: totalPages
          };
        })
      );
  }

  public getContractFlatten(id: number, beginDate = DATE_MIN, endDate = DATE_MAX): Observable<ContractFlattenDTO> {
    return this.api.ga20.get(`/contract/${id}/flatten?begindate=${beginDate}&enddate=${endDate}`);
  }

  public uniqueEmailAsyncValidator(empId?: number): AsyncValidatorFn {
    return (control: UntypedFormControl): Observable<ValidationErrors> => {
      if (!control.value) {
        return of(null);
      }
      return this.checkEmailAvailable(control.value, empId)
        .pipe(
          map((status) => {
            if (!status) {
              return {emailExists: true};
            }
            return null;
          }));
    };
  }

  getEntityPath(value: any): Observable<string> {
    return this.api.get(`/ga/api/v1/entity/${value}/path`, REQUEST_OPTIONS.RESPONSE_TYPE.TEXT);
  }

  public getEmployeeFileActionButtonsDisabled(): Observable<{ action: string; value: boolean }> {
    return this.employeeFileActionButtonsDisabled.asObservable();
  }

  public setEmployeeFileActionButtonsDisabled(toggleButtonAction: { action: string; value: boolean }): void {
    this.employeeFileActionButtonsDisabled.next(toggleButtonAction);
  }

  public getRecalculationRequested(): Observable<void> {
    return this.recalculationRequested.asObservable();
  }

  public requestRecalculation(): void {
    this.recalculationRequested.next();
  }

}
